'use client'

import { cn } from '@/lib/utils'
import { TCardFacet, TFacet } from '@/types/collection'
import { FilterValues } from '../../../types/filter-store-v2.types'
import { shouldAddNofollowToFilterLinks } from '../../../utils/filter-utils'
import { buildFilterUrl } from './filter-url-builder'

type Props = {
  attribute: TFacet
  onFilterChange: (value: string) => void
  value?: string
  originalSlug?: string
  currentFilters?: FilterValues
  facets?: TFacet[]
  cardFacets?: TCardFacet[]
}

export const FilterColors = ({
  onFilterChange,
  value,
  attribute,
  originalSlug,
  currentFilters = {},
  facets,
  cardFacets,
}: Props) => {
  const handleColorClick = (clickedValue: string) => {
    const newValue = value === clickedValue ? undefined : clickedValue
    onFilterChange(newValue as string)
  }

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    colorValue: string
  ) => {
    e.preventDefault()
    handleColorClick(colorValue)
  }

  // Check if should add nofollow
  const shouldAddNofollow = shouldAddNofollowToFilterLinks(
    currentFilters,
    facets,
    cardFacets
  )

  // Build URL for each color option
  const getFilterUrl = (colorValue: string): string => {
    if (!originalSlug) return '#'
    const isChecked = value === colorValue
    return buildFilterUrl({
      originalSlug,
      currentFilters,
      facetKey: 'color',
      facetValue: colorValue,
      isChecked: !isChecked, // URL for toggled state
      isMultiple: false, // Color is single value (but can toggle)
      isConcatSlug: attribute.is_concat_slug ?? false,
      facets,
      cardFacets,
    })
  }

  const linear =
    'linear-gradient(226.4deg, rgba(255, 26, 1, 1) 28.9%, rgba(254, 155, 1, 1) 33%, rgba(255, 241, 0, 1) 48.6%, rgba(34, 218, 1, 1) 65.3%, rgba(0, 141, 254, 1) 80.6%, rgba(113, 63, 254, 1) 100.1%)'

  return (
    <ul className='grid grid-cols-5 gap-2 md:grid-cols-4'>
      {attribute.facet_values?.map((color, index) => {
        const filterUrl = getFilterUrl(color.seo_alias)
        return (
          <ColorItem
            key={index}
            color={color.title}
            style={{
              background:
                color?.seo_alias === 'phoi-mau' ? linear : color.hex_code,
            }}
            isActive={value === color.seo_alias}
            filterUrl={filterUrl}
            onLinkClick={(e) => handleLinkClick(e, color.seo_alias)}
            shouldAddNofollow={shouldAddNofollow}
          />
        )
      })}
    </ul>
  )
}

const ColorItem = ({
  color,
  className,
  isActive,
  style,
  filterUrl,
  onLinkClick,
  shouldAddNofollow,
}: {
  color: string
  className?: string
  isActive: boolean
  style?: React.CSSProperties
  filterUrl: string
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  shouldAddNofollow: boolean
}) => {
  return (
    <li className='w-15 min-w-15'>
      <a
        href={filterUrl}
        onClick={onLinkClick}
        className='group flex w-full flex-col items-center justify-start no-underline'
        aria-label={`Filter by color ${color}`}
        rel={shouldAddNofollow ? 'nofollow' : undefined}
      >
        <span
          className={cn(
            'block h-6.5 w-6.5 rounded-full border bg-light',
            isActive
              ? 'border-primary-500'
              : 'border-gray-400 group-hover:border-primary-500',
            className
          )}
          style={style}
        ></span>
        <p
          className={cn(
            'mt-1 text-xs leading-3.5',
            isActive
              ? 'text-primary-500'
              : 'text-neutral-500 group-hover:text-primary-500'
          )}
        >
          {color}
        </p>
      </a>
    </li>
  )
}
